// DEMO ACTIONS — deterministic operations on the REAL controls (found by their stable `data-tour` anchors). The student watches the actual
// interface being used: text is typed into the real fields, the real buttons are clicked; the demo backend answers. No network, no LLM.
export const reducedMotion = () => { try { return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; } };
// THE LIVE ANCHOR — of every element carrying this `data-tour` name, the one the student can actually see: never a `hidden` one, never an
// element with no box; when the demo workspace is open, the copy INSIDE it (the real workspace underneath the demo may carry the same anchors);
// otherwise the last visible copy (the topmost layer renders last). Measured fresh on every call — nothing is cached.
const DEMO_ROOT = '[data-onboarding-stage]';
const visible = (el) => !el.closest("[hidden]") && (el.getClientRects().length > 0 || !!el.offsetParent || el === document.body);
export const anchor = (name) => {
  const all = [...document.querySelectorAll(`[data-tour="${name}"]`)].filter(visible);
  if (!all.length) return document.querySelector(`[data-tour="${name}"]`);
  const demo = document.querySelector(DEMO_ROOT);
  const inDemo = demo ? all.filter((el) => demo.contains(el)) : [];
  return (inDemo.length ? inDemo : all).slice(-1)[0];
};
const sleep = (ms) => new Promise((r) => setTimeout(r, reducedMotion() ? 0 : ms));
export const waitFor = (fn, ms = 4000, every = 60) => new Promise((resolve) => {
  const t0 = Date.now();
  const tick = () => { const v = fn(); if (v) return resolve(v); if (Date.now() - t0 > ms) return resolve(null); setTimeout(tick, every); };
  tick();
});
export const waitForAnchor = (name, ms) => waitFor(() => anchor(name), ms);

// React-controlled <textarea>/<input>: set through the native setter so the change is seen as user input
export function setFieldValue(el, value) {
  if (!el) return;
  const proto = el.tagName === "TEXTAREA" ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, "value").set;
  setter.call(el, value);
  el.dispatchEvent(new Event("input", { bubbles: true }));
}
// the writing editor (contentEditable): the words appear in 2–3 natural chunks, never letter by letter
export const chunks = (text, n = 3) => { const w = text.split(" "); const size = Math.ceil(w.length / n); const out = []; for (let i = 0; i < w.length; i += size) out.push(w.slice(0, i + size).join(" ")); return out; };
export async function typeEditable(el, text, delay = 420) {
  if (!el) return;
  el.focus();
  for (const part of chunks(text)) { el.textContent = part; el.dispatchEvent(new Event("input", { bubbles: true })); await sleep(delay); }
}
export async function typeField(el, text, delay = 420) {
  if (!el) return;
  el.focus();
  for (const part of chunks(text)) { setFieldValue(el, part); await sleep(delay); }
}
export const click = (el) => { if (el) el.click(); return !!el; };

export const demoActions = {
  async setAssignment(text) { const el = await waitForAnchor("assignment", 1500); if (el) await typeField(el, text, 500); return !!el; },
  async setEssayText(text) {
    if (!anchor("essay-add-input-box") && !document.querySelector('[data-testid="essay-add-input"]')) click(anchor("essay-write-next"));
    const ed = await waitFor(() => document.querySelector('[data-testid="essay-add-input"]'));
    if (!ed) return false;
    await typeEditable(ed, text);
    await sleep(300);
    click(anchor("essay-add-save"));
    return true;
  },
  async addStudentMessage(text, { send = true } = {}) {
    const box = await waitForAnchor("coach-input", 1500);
    if (!box) return false;
    await typeField(box, text, 350);
    if (send) { await sleep(200); click(anchor("coach-send")); }
    return true;
  },
  async waitForCoachText(fragment, ms = 6000) { return waitFor(() => [...document.querySelectorAll('[data-tour="coach-dialogue"] *')].some((n) => n.childElementCount === 0 && (n.textContent || "").includes(fragment)), ms); },
  async openStructure() {
    if (!anchor("view-structure") || anchor("view-structure").closest("[hidden]")) { click(anchor("tools-menu-button") || document.querySelector('[data-testid="tools-menu-button"]')); await waitFor(() => anchor("view-structure") && !anchor("view-structure").closest("[hidden]"), 1500); } click(anchor("view-structure")); return !!(await waitForAnchor("structure-map")); },
  async selectFirstUnit() { const card = await waitFor(() => document.querySelector('[data-tour="structure-map"] [data-testid^="mm-idea-"]'), 2000); if (card) card.click(); return !!card; },
  async returnToEssay() { click(anchor("view-essay")); return !!(await waitForAnchor("essay-editor")); },
  async openBigPicture() {                                                    // Tools ▾ → Big Picture (the persistent bar is gone); no-op when TWS already opened it
    if (anchor("big-picture-left")) return true;
    click(document.querySelector('[data-testid="tools-menu-button"]')); await waitFor(() => document.querySelector('[data-testid="tools-big-picture"]'), 800);
    click(document.querySelector('[data-testid="tools-big-picture"]')); return !!(await waitForAnchor("big-picture-left"));
  },
  async closeBigPicture() { click(anchor("big-picture-return")); return !!(await waitForAnchor("essay-editor")); },
};
